import React, { useEffect, useState, useCallback } from 'react';
import {
  Card,
  Form,
  Input,
  DatePicker,
  Select,
  Button,
  Space,
  message,
  Typography,
  Row,
  Col,
  Checkbox,
  Upload,
  Table,
  Tag,
  Tooltip,
  Tabs,
  Progress,
  Rate,
} from 'antd';
import {
  ArrowLeftOutlined,
  CheckOutlined,
  PlusOutlined,
  DeleteOutlined,
  EyeOutlined,
  DownloadOutlined,
} from '@ant-design/icons';
import { useNavigate, useParams, useSearchParams, useOutletContext } from 'react-router-dom';
import { Alert } from 'flowbite-react';
import { useAuth } from '../contexts/AuthContext';
import { hasPermission } from '../utils/permissions';
import {
  letterService,
  letterItemService,
  letterCommentService,
  letterReviewService,
  Letter,
  CreateLetterData,
  LetterSubTask,
  LetterItem,
  LetterComment,
  LetterReview,
} from '../services/letters';
import { customerService } from '../services/customers';
import { businessService } from '../services/businesses';
import { taskTypeService, TaskType } from '../services/taskTypes';
import { taskTemplateService, TaskTemplate } from '../services/taskTemplates';
import { SubTaskTemplate, Task, taskService } from '../services/tasks';
import api from '../services/api';
import dayjs from 'dayjs';

const { Title, Text } = Typography;
const { TextArea } = Input;
const { TabPane } = Tabs;

type LayoutHeaderContext = {
  setHeaderContent: (content: React.ReactNode) => void;
};

export const LetterFormPage: React.FC = () => {
  const { id } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const isEdit = Boolean(id);
  const { setHeaderContent } = useOutletContext<LayoutHeaderContext>();
  const isCreateMode = !isEdit;
  const [form] = Form.useForm();
  const { user } = useAuth();
  const canDelete = hasPermission(user, 'projects.letters.delete');
  const canApprove = hasPermission(user, 'projects.letters.verify');
  const canViewDetails = hasPermission(user, 'projects.letters.details.view');
  const canCreateDetails = hasPermission(user, 'projects.letters.details.create');
  const canEditDetails = hasPermission(user, 'projects.letters.details.update');
  const canViewChecklist = hasPermission(user, 'projects.letters.checklist.view');
  const canCreateChecklist = hasPermission(user, 'projects.letters.checklist.create');
  const canEditChecklist = hasPermission(user, 'projects.letters.checklist.update');
  const canViewLetter = hasPermission(user, 'projects.letters.letter.view');
  const canCreateLetter = hasPermission(user, 'projects.letters.letter.create');
  const canEditLetter = hasPermission(user, 'projects.letters.letter.update');
  const canViewComment = hasPermission(user, 'projects.letters.comment.view');
  const canCreateComment = hasPermission(user, 'projects.letters.comment.create');
  const canEditComment = hasPermission(user, 'projects.letters.comment.update');
  const canViewReview = hasPermission(user, 'projects.letters.review.view');
  const canCreateReview = hasPermission(user, 'projects.letters.review.create');
  const canEditReview = hasPermission(user, 'projects.letters.review.update');
  const canEditAny = [
    canEditDetails,
    canEditChecklist,
    canEditLetter,
    canEditComment,
    canEditReview,
  ].some(Boolean);
  const [submitting, setSubmitting] = useState(false);
  const [loadingLetter, setLoadingLetter] = useState(false);
  const [submitNotice, setSubmitNotice] = useState<{ type: 'success' | 'failure'; message: string } | null>(null);
  const [approving, setApproving] = useState(false);
  const [currentStatus, setCurrentStatus] = useState<string>('Plan');
  const [activeTab, setActiveTab] = useState<string>(searchParams.get('tab') || 'overview');
  const [customers, setCustomers] = useState<any[]>([]);
  const [businesses, setBusinesses] = useState<any[]>([]);
  const [customerType, setCustomerType] = useState<'personal' | 'business'>('personal');
  const [selectedCustomer, setSelectedCustomer] = useState<any>(null);
  const [selectedBusiness, setSelectedBusiness] = useState<any>(null);
  const [taskTypes, setTaskTypes] = useState<TaskType[]>([]);
  const [subTaskTemplates, setSubTaskTemplates] = useState<SubTaskTemplate[]>([]);
  const [taskTemplates, setTaskTemplates] = useState<TaskTemplate[]>([]);
  const [tasks, setTasks] = useState<Task[]>([]);
  const [letterSubTasks, setLetterSubTasks] = useState<LetterSubTask[]>([]);
  const [letterItems, setLetterItems] = useState<Array<LetterItem | { id: string; letter_id: number; attachment: null; enabled: boolean; created_at: string; creator?: any; file: File; batch_no?: number; approval_status?: 'pending' | 'approved' | 'rejected' }>>([]);
  const [comments, setComments] = useState<LetterComment[]>([]);
  const [reviews, setReviews] = useState<LetterReview[]>([]);
  const [commentText, setCommentText] = useState('');
  const [reviewText, setReviewText] = useState('');
  const [reviewRating, setReviewRating] = useState<number | null>(null);

  useEffect(() => {
    setHeaderContent(
      <span style={{ fontSize: 16, fontWeight: 600, color: '#111827' }}>
        Letters
      </span>
    );
    return () => setHeaderContent(null);
  }, [setHeaderContent]);
  const [uploadQueue, setUploadQueue] = useState<Array<{ file: File; id: string }>>([]);
  const [uploadStatus, setUploadStatus] = useState<Record<string, 'pending' | 'uploading' | 'success' | 'error'>>({});
  const [uploadProgress, setUploadProgress] = useState<Record<string, number>>({});
  const [letterName, setLetterName] = useState<string>('');
  const [letterSender, setLetterSender] = useState<{ id?: number; email?: string; name?: string } | null>(null);
  const [isEditing, setIsEditing] = useState(!isEdit); // For new letters, default to editing mode
  const navigate = useNavigate();
  const filterGeneralSubTasks = (items: any[]) =>
    items.filter((st: any) => !st.list_type || st.list_type === 'general');
  const normalizeLetterSubTasks = useCallback((rawSubTasks: any[]): LetterSubTask[] => {
    return rawSubTasks.map((st: any) => ({
      id: st.id,
      letter_id: st.letter_id,
      type: st.type,
      name: st.name,
      completed: Boolean(st.completed),
      created_by: st.created_by,
      updated_by: st.updated_by,
      created_at: st.created_at,
      updated_at: st.updated_at,
      creator: st.creator || undefined,
      updater: st.updater || undefined,
    }));
  }, []);
  const mapStatus = useCallback((status: string | undefined): 'Plan' | 'Unread' | 'Read' => {
    if (!status) return 'Plan';
    if (status === 'draft') return 'Plan';
    if (status === 'sent') return 'Unread';
    if (['Plan', 'Unread', 'Read'].includes(status)) return status as 'Plan' | 'Unread' | 'Read';
    return 'Plan';
  }, []);
  const watchedStatus = Form.useWatch('status', form);
  const watchedIsSent = Form.useWatch('is_sent', form);
  const headerStatus = mapStatus((watchedStatus as string | undefined) ?? currentStatus);
  const showReadButton = headerStatus === 'Unread' && Boolean(watchedIsSent);

  useEffect(() => {
    fetchInitialData();
    if (isEdit && id) {
      loadLetter(parseInt(id, 10));
    } else {
      // Set default status for new letters
      form.setFieldsValue({ status: 'Plan', is_sent: false });
    }
  }, [isEdit, id, user?.id]);

  // Sync activeTab with URL parameter
  useEffect(() => {
    const tabFromUrl = searchParams.get('tab');
    if (tabFromUrl) {
      setActiveTab(tabFromUrl);
    }
  }, [searchParams]);

  const allowViewDetails = isCreateMode ? canCreateDetails : canViewDetails;
  const allowEditDetails = isEditing && (isCreateMode ? canCreateDetails : canEditDetails);
  const allowViewChecklist = isCreateMode ? canCreateChecklist : canViewChecklist;
  const allowEditChecklist = isEditing && (isCreateMode ? canCreateChecklist : canEditChecklist);
  const allowViewLetter = isCreateMode ? canCreateLetter : canViewLetter;
  const allowEditLetter = isEditing && (isCreateMode ? canCreateLetter : canEditLetter);
  const allowViewComment = isCreateMode ? canCreateComment : canViewComment;
  const allowEditComment = isEditing && (isCreateMode ? canCreateComment : canEditComment);
  const allowViewReview = isCreateMode ? canCreateReview : canViewReview;
  const allowEditReview = isEditing && (isCreateMode ? canCreateReview : canEditReview);
  const canEditRecord = Boolean(canEditAny);

  const tabVisibility = [
    { key: 'overview', visible: allowViewDetails },
    { key: 'checklist', visible: allowViewChecklist },
    { key: 'letter', visible: allowViewLetter },
    { key: 'comment', visible: allowViewComment },
    { key: 'review', visible: allowViewReview },
  ];

  useEffect(() => {
    const current = tabVisibility.find((tab) => tab.key === activeTab);
    if (current?.visible) {
      return;
    }
    const fallback = tabVisibility.find((tab) => tab.visible)?.key || 'overview';
    if (fallback !== activeTab) {
      setActiveTab(fallback);
      setSearchParams({ tab: fallback });
    }
  }, [
    activeTab,
    allowViewDetails,
    allowViewChecklist,
    allowViewLetter,
    allowViewComment,
    allowViewReview,
    setSearchParams,
  ]);

  // Handle tab change and update URL
  const handleTabChange = (key: string) => {
    setActiveTab(key);
    setSearchParams({ tab: key });
  };

  const fetchInitialData = async () => {
    try {
      const [customersResponse, businessesResponse, taskTypesResponse, subTasksResponse, taskTemplatesResponse, tasksResponse] = await Promise.all([
        customerService.getAll(),
        businessService.getAll().catch(() => ({ data: [] })),
        taskTypeService.getAll().catch(() => ({ data: [] })),
        api.get('/sub-tasks').catch(() => ({ data: { data: [] } })),
        taskTemplateService.getAll().catch(() => ({ data: [] })),
        taskService.getAll().catch(() => ({ data: [] })),
      ]);
      setCustomers(customersResponse.data || customersResponse || []);
      setBusinesses(businessesResponse.data || []);
      setTaskTypes(taskTypesResponse.data || taskTypesResponse || []);
      setSubTaskTemplates(filterGeneralSubTasks(subTasksResponse.data?.data || subTasksResponse.data || []));
      setTaskTemplates(taskTemplatesResponse.data || taskTemplatesResponse || []);
      setTasks(tasksResponse.data || []);
    } catch {
      message.error('Failed to load initial data');
    }
  };

  const loadLetter = async (letterId: number) => {
    setLoadingLetter(true);
    try {
      const letter = await letterService.getById(letterId);
      setLetterSender(letter.sender ? {
        id: letter.sender.id,
        email: letter.sender.email,
        name: letter.sender.name,
      } : null);
      const resolvedType = (letter as any).type ?? ((letter as any).business_id ? 'business' : 'personal');
      const resolvedReferenceId = (letter as any).reference_id ?? ((letter as any).business_id ?? letter.customer_id);
      const mappedStatus = mapStatus(letter.status);
      form.setFieldsValue({
        customer_id: letter.customer_id,
        reference_number: letter.reference_number ?? undefined,
        task_type_id: (letter as any).task_type_id ?? undefined,
        task_template_id: (letter as any).task_template_id ?? undefined,
        task_id: (letter as any).task_id ?? (letter as any).task?.id ?? undefined,
        create_date: letter.create_date ? dayjs(letter.create_date) : null,
        due_date: letter.due_date ? dayjs(letter.due_date) : null,
        status: mappedStatus,
        is_sent: (letter as any).is_sent !== undefined ? (letter as any).is_sent : false,
        type: resolvedType,
        reference_id: resolvedReferenceId,
      });
      setCurrentStatus(mappedStatus);
      
      setCustomerType(resolvedType);
      // Handle business_id
      if (resolvedType === 'business' && resolvedReferenceId) {
        form.setFieldsValue({ business_id: resolvedReferenceId, reference_id: resolvedReferenceId });
        let business = businesses.find(b => b.id === resolvedReferenceId)
          || businesses.find(b => b.id === (letter as any).business_id);
        if (!business) {
          try {
            business = await businessService.getById(resolvedReferenceId);
          } catch {
            business = undefined;
          }
        }
        if (business) {
          setSelectedBusiness(business);
          if (business.customer_id) {
            try {
              const customer = await customerService.getById(business.customer_id);
              setSelectedCustomer(customer);
              form.setFieldsValue({ customer_id: business.customer_id });
            } catch {
              // Ignore error
            }
          }
        }
      } else if (resolvedType === 'personal' && resolvedReferenceId) {
        form.setFieldsValue({ customer_id: resolvedReferenceId, reference_id: resolvedReferenceId });
        try {
          const customer = await customerService.getById(resolvedReferenceId);
          setSelectedCustomer(customer);
        } catch {
          // Ignore error
        }
      }
      
      const rawSubTasks = (letter.subTasks || (letter as any).sub_tasks || []) as any[];
      setLetterSubTasks(normalizeLetterSubTasks(rawSubTasks));
      setLetterItems(letter.items || []);
      setComments(letter.comments || []);
      setReviews(letter.reviews || []);
      
      // Set letter name for header display
      const formatLetterNumber = (id: number, referenceNumber?: string) =>
        referenceNumber || `#${id.toString().padStart(7, '0')}`;
      const resolvedTaskTemplateId = (letter as any).task_template_id
        ?? taskTemplates.find(t => t.name === letter.subject?.name)?.id;
      if (letter.customer_id && resolvedTaskTemplateId) {
        form.setFieldsValue({ task_template_id: resolvedTaskTemplateId });
        setLetterName(formatLetterNumber(letterId, letter.reference_number));
      } else {
        setLetterName(formatLetterNumber(letterId, letter.reference_number));
      }
    } catch {
      message.error('Failed to load letter');
    } finally {
      setLoadingLetter(false);
    }
  };

  const handleCustomerTypeChange = (type: 'personal' | 'business') => {
    setCustomerType(type);
    setSelectedCustomer(null);
    setSelectedBusiness(null);
    form.setFieldsValue({
      type,
      reference_id: undefined,
      customer_id: undefined,
      business_id: undefined,
      task_id: undefined,
    });
  };

  const handleBusinessChange = async (businessId: number) => {
    if (businessId) {
      try {
        const business = await businessService.getById(businessId);
        setSelectedBusiness(business);
        if (business.customer_id) {
          const customer = await customerService.getById(business.customer_id);
          setSelectedCustomer(customer);
          console.log('Advisor loaded (business):', {
            businessId,
            advisor: business.advisorStaff?.name || business.advisor_staff?.name || '',
          });
          form.setFieldsValue({
            customer_id: business.customer_id,
            reference_id: businessId,
          });
        } else {
          console.log('Advisor loaded (business):', {
            businessId,
            advisor: business.advisorStaff?.name || business.advisor_staff?.name || '',
          });
          form.setFieldsValue({
            customer_id: undefined,
            reference_id: businessId,
          });
        }
      } catch {
        message.error('Failed to load business details');
      }
    } else {
      setSelectedBusiness(null);
      setSelectedCustomer(null);
      form.setFieldsValue({
        customer_id: undefined,
        reference_id: undefined,
      });
    }
  };

  const handleCustomerChange = async (customerId: number) => {
    if (customerId) {
      try {
        const customer = await customerService.getById(customerId);
        setSelectedCustomer(customer);
        console.log('Advisor loaded (customer):', {
          customerId,
          advisor: customer.advisorStaff?.name || customer.advisor_staff?.name || '',
        });
        form.setFieldsValue({ reference_id: customerId });
      } catch {
        message.error('Failed to load customer');
      }
    } else {
      setSelectedCustomer(null);
      form.setFieldsValue({ reference_id: undefined });
    }
  };

  const formatTaskNumber = (taskId: number, referenceNumber?: string) => {
    return referenceNumber || `#${taskId.toString().padStart(7, '0')}`;
  };

  const getAvailableTasks = () => {
    const customerId = form.getFieldValue('customer_id');
    const businessId = form.getFieldValue('business_id');
    if (businessId) {
      return tasks.filter((task) => task.business_id === businessId);
    }
    if (customerId) {
      return tasks.filter((task) => task.customer_id === customerId);
    }
    return tasks;
  };

  const handleTaskTemplateChange = (taskTemplateId: number | null) => {
    if (!taskTemplateId) {
      setLetterSubTasks([]);
      return;
    }
    const matchedTemplates = subTaskTemplates.filter((st) => st.task_template_id === taskTemplateId);
    if (matchedTemplates.length === 0) {
      setLetterSubTasks([]);
      return;
    }
    const now = Date.now();
    const mapped = matchedTemplates.map((st, idx) => ({
      id: `temp-subtask-${now}-${idx}`,
      letter_id: id ? parseInt(id, 10) : 0,
      type: 'letters',
      name: st.name || '',
      completed: false,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
    })) as any[];
    setLetterSubTasks(mapped);
  };

  const handleTaskChange = (taskId?: number) => {
    if (!taskId) {
      form.setFieldsValue({ task_id: undefined });
      return;
    }
    const selectedTask = tasks.find((task) => task.id === taskId);
    form.setFieldsValue({
      task_id: taskId,
      task_type_id: selectedTask?.task_type_id ?? form.getFieldValue('task_type_id'),
      task_template_id: selectedTask?.task_template_id ?? form.getFieldValue('task_template_id'),
    });
    if (selectedTask?.task_template_id) {
      handleTaskTemplateChange(selectedTask.task_template_id);
    }
  };

  const handleAddSubTask = () => {
    if (!allowEditChecklist) {
      message.warning('You do not have permission to edit checklist');
      return;
    }
    const newSubTask: any = {
      id: `temp-subtask-${Date.now()}`,
      letter_id: id ? parseInt(id, 10) : 0,
      type: 'letters',
      name: '',
      completed: false,
    };
    setLetterSubTasks(prev => [...prev, newSubTask]);
  };

  const handleAddComment = () => {
    if (!allowEditComment) {
      message.warning('You do not have permission to add comments');
      return;
    }
    if (!commentText.trim()) {
      message.error('Please enter a comment');
      return;
    }
    const newComment: any = {
      id: `temp-comment-${Date.now()}`,
      letter_id: id ? parseInt(id, 10) : 0,
      comment: commentText,
      created_at: new Date().toISOString(),
      user: { name: user?.name || 'Current User' },
    };
    setComments(prev => [...prev, newComment]);
    setCommentText('');
    message.success('Comment added (will be saved on submit)');
  };

  const handleDeleteComment = async (commentId: number | string) => {
    if (!canDelete) {
      message.warning('You do not have permission to delete comments');
      return;
    }
    if (typeof commentId === 'number') {
      try {
        await letterCommentService.delete(commentId);
        setComments(prev => prev.filter(c => String(c.id) !== String(commentId)));
        message.success('Comment deleted');
        return;
      } catch {
        message.error('Failed to delete comment');
        if (id) {
          loadLetter(parseInt(id, 10));
        }
        return;
      }
    }
    setComments(prev => prev.filter(c => String(c.id) !== String(commentId)));
  };

  const handleAddReview = () => {
    if (!allowEditReview) {
      message.warning('You do not have permission to add reviews');
      return;
    }
    if (!reviewText.trim()) {
      message.error('Please enter a review');
      return;
    }
    if (!reviewRating) {
      message.error('Please select a rating');
      return;
    }
    const newReview: any = {
      id: `temp-review-${Date.now()}`,
      letter_id: id ? parseInt(id, 10) : 0,
      customer_id: undefined,
      review: reviewText,
      rating: reviewRating,
      created_at: new Date().toISOString(),
      creator: { name: user?.name || 'Current User' },
    };
    setReviews(prev => [...prev, newReview]);
    setReviewText('');
    setReviewRating(null);
    message.success('Review added (will be saved on submit)');
  };

  const handleAddLetterItem = (file: File, batchNo: number = 1) => {
    if (!allowEditLetter) {
      message.warning('You do not have permission to add letters');
      return;
    }
    // Add to local state only, don't upload to backend yet
    const tempId = `temp-letter-${Date.now()}-${Math.random()}`;
    const newItem: any = {
      id: tempId,
      letter_id: id ? parseInt(id, 10) : 0,
      attachment: null,
      enabled: true,
      batch_no: batchNo,
      approval_status: 'pending',
      created_at: new Date().toISOString(),
      creator: user ? { id: user.id, name: user.name, email: user.email || '' } : undefined,
      file: file, // Store the file object for later upload
    };
    
    // Add to local state
    setLetterItems(prev => [newItem, ...prev]);
    
    message.success('Letter item added (will be saved on submit)');
  };

  const handleDeleteLetterItem = (itemId: number | string) => {
    if (!canDelete) {
      message.warning('You do not have permission to delete letters');
      return;
    }
    // If it's a temp ID (string), just remove from local state
    if (typeof itemId === 'string' && itemId.startsWith('temp-letter-')) {
      setLetterItems(prev => prev.filter(item => {
        const iId = item.id;
        if (typeof iId === 'string') {
          return iId !== itemId;
      }
        return true; // Keep non-string IDs
      }));
      // Remove from upload queue
      setUploadQueue(prev => prev.filter(item => item.id !== itemId));
      setUploadStatus(prev => {
        const newStatus = { ...prev };
        delete newStatus[itemId];
        return newStatus;
      });
      setUploadProgress(prev => {
        const newProgress = { ...prev };
        delete newProgress[itemId];
        return newProgress;
      });
      message.success('Letter item removed');
      return;
    }
    
    // If it's an existing item (number), delete immediately
    if (typeof itemId === 'number') {
      setLetterItems(prev => prev.filter(item => {
        const iId = item.id;
        if (typeof iId === 'number') {
          return iId !== itemId;
    }
        return true; // Keep non-number IDs
      }));
      // Delete from backend immediately
      letterItemService.delete(itemId).then(() => {
      message.success('Letter item deleted successfully');
      }).catch(() => {
      message.error('Failed to delete letter item');
        // Reload the items if deletion failed
        if (id) {
          letterService.getById(parseInt(id, 10)).then(letter => {
            setLetterItems(letter.items || []);
          });
        }
      });
    }
  };

  const normalizeLetterBatchNo = (item: any) => item?.batch_no || 1;
  const getLetterBatchItems = (batchNo: number) =>
    letterItems.filter((item) => normalizeLetterBatchNo(item) === batchNo);
  const getLetterBatchStatus = (items: any[]) => {
    if (!items.length) {
      return 'pending';
    }
    const latest = [...items].sort((a, b) => {
      const aTime = new Date(a.created_at || 0).getTime();
      const bTime = new Date(b.created_at || 0).getTime();
      return bTime - aTime;
    })[0];
    return latest?.approval_status || 'pending';
  };
  const getLetterStatusTag = (status: 'pending' | 'approved' | 'rejected') => {
    if (status === 'approved') return <Tag color="green">Approved</Tag>;
    if (status === 'rejected') return <Tag color="red">Rejected</Tag>;
    return <Tag color="orange">Pending</Tag>;
  };
  const updateLetterBatchStatus = async (batchNo: number, status: 'approved' | 'rejected') => {
    const batchItems = getLetterBatchItems(batchNo);
    if (batchItems.length === 0) {
      return;
    }
    try {
      const updates = batchItems
        .filter((item) => typeof item.id === 'number')
        .map((item) => api.put(`/letter-items/${item.id}`, { approval_status: status }));
      if (updates.length > 0) {
        await Promise.all(updates);
      }
      setLetterItems(prev =>
        prev.map((item: any) => {
          const itemBatch = item.batch_no || 1;
          if (itemBatch !== batchNo) return item;
          return { ...item, approval_status: status };
        }),
      );
      message.success(`Batch ${batchNo} ${status}`);
    } catch {
      message.error('Failed to update approval status');
    }
  };
  const renderLetterStatusActions = (batchNo: number, status: 'pending' | 'approved' | 'rejected', hasItems: boolean) => {
    if (!canApprove || !hasItems || status !== 'pending') {
      return null;
    }
    return (
      <Space size={6}>
        <Button size="small" onClick={() => updateLetterBatchStatus(batchNo, 'approved')}>
          Approve
        </Button>
        <Button size="small" danger onClick={() => updateLetterBatchStatus(batchNo, 'rejected')}>
          Reject
        </Button>
      </Space>
    );
  };
  const renderLetterUploadButton = (batchNo: number, disabled?: boolean) => (
    <Upload
      beforeUpload={(file) => {
        handleAddLetterItem(file, batchNo);
        return false;
      }}
      showUploadList={false}
      disabled={disabled}
    >
      <Button type="link" icon={<PlusOutlined />} disabled={disabled}>
        Add
      </Button>
    </Upload>
  );
  const renderLetterItemList = (items: typeof letterItems) => {
    if (items.length === 0) {
      return (
        <div style={{ color: '#999', textAlign: 'center', padding: '12px 0' }}>
          No letter items
        </div>
      );
    }

    return items.map((item) => {
      const itemId: number | string = item.id;
      const isTemp = typeof itemId === 'string' && itemId.startsWith('temp-letter-');
      const fileName = isTemp
        ? ('file' in item ? (item as any).file?.name : 'New file')
        : (item.attachment ? item.attachment.split('/').pop() : 'No attachment');

      return (
        <div key={item.id} style={{ padding: '12px 0', borderBottom: '1px solid #f0f0f0' }}>
          <div style={{ marginBottom: 4 }}>
            {isTemp ? (
              <Space>
                <Text>{fileName}</Text>
                <Tag color="orange">Pending</Tag>
                {allowEditLetter && canDelete && (
                  <Button
                    type="link"
                    danger
                    icon={<DeleteOutlined />}
                    onClick={() => handleDeleteLetterItem(item.id!)}
                    size="small"
                  />
                )}
              </Space>
            ) : item.attachment ? (
              <Space>
                <Button
                  type="link"
                  icon={<EyeOutlined />}
                  disabled={false}
                  onClick={() => handleViewLetterItem(item.id!)}
                  size="small"
                >
                  {fileName}
                </Button>
                <Button
                  type="link"
                  icon={<DownloadOutlined />}
                  disabled={false}
                  onClick={() => handleDownloadLetterItem(item.id!)}
                  size="small"
                />
                {allowEditLetter && canDelete && (
                  <Button
                    type="link"
                    danger
                    icon={<DeleteOutlined />}
                    onClick={() => handleDeleteLetterItem(item.id!)}
                    size="small"
                  />
                )}
              </Space>
            ) : (
              <Text strong>No attachment</Text>
            )}
          </div>
          <div style={{ fontSize: '12px', color: '#999' }}>
            By {item.creator?.name || 'Unknown'} {formatDateTime(item.created_at)}
          </div>
        </div>
      );
    });
  };

  const batch1Items = getLetterBatchItems(1);
  const batch2Items = getLetterBatchItems(2);
  const batch3Items = getLetterBatchItems(3);
  const batch1Status = getLetterBatchStatus(batch1Items) as 'pending' | 'approved' | 'rejected';
  const batch2Status = getLetterBatchStatus(batch2Items) as 'pending' | 'approved' | 'rejected';
  const batch3Status = getLetterBatchStatus(batch3Items) as 'pending' | 'approved' | 'rejected';
  const allowBatch2 = batch1Status === 'rejected';
  const allowBatch3 = batch2Status === 'rejected';
  const showBatch2 = allowBatch2 || batch2Items.length > 0;
  const showBatch3 = allowBatch3 || batch3Items.length > 0;

  const handleViewLetterItem = async (itemId: number) => {
    try {
      const blob = await letterItemService.view(itemId);
      const url = window.URL.createObjectURL(blob);
      window.open(url, '_blank');
      // Clean up the URL after a delay
      setTimeout(() => window.URL.revokeObjectURL(url), 100);
    } catch {
      message.error('Failed to view attachment');
    }
  };

  const handleDownloadLetterItem = async (itemId: number | string) => {
    // If it's a temp ID, don't try to download
    if (typeof itemId === 'string' && itemId.startsWith('temp-letter-')) {
      return;
    }
    if (typeof itemId !== 'number') return;
    try {
      const blob = await letterItemService.view(itemId);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const item = letterItems.find(i => i.id === itemId);
      const filename = item?.attachment ? item.attachment.split('/').pop() || 'attachment' : 'attachment';
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      message.success('Attachment downloaded successfully');
    } catch {
      message.error('Failed to download attachment');
    }
  };

  const handleSubmit = async (values: any) => {
    setSubmitting(true);
    try {
      const referenceId = customerType === 'business' ? values.business_id : values.customer_id;
      const payload: CreateLetterData = {
        ...values,
        create_date: values.create_date?.format('YYYY-MM-DD'),
        due_date: values.due_date?.format('YYYY-MM-DD'),
        // If business_id is selected, use business's customer_id
        type: customerType,
        reference_id: referenceId,
        customer_id: values.business_id ? selectedBusiness?.customer_id : values.customer_id,
        business_id: values.business_id || undefined,
        // Ensure status is 'Plan' for new letters
        status: values.status || 'Plan',
        is_sent: values.is_sent !== undefined ? values.is_sent : false,
        sub_tasks: letterSubTasks
          .filter(st => st.name && st.name.trim())
          .map(st => ({
            ...(typeof st.id === 'number' ? { id: st.id } : {}),
            name: st.name,
            completed: st.completed || false,
          })),
      };

      let letterId: number;
      if (isEdit && id) {
        await letterService.update(parseInt(id, 10), payload);
        letterId = parseInt(id, 10);
      } else {
        const newLetter = await letterService.create(payload);
        letterId = newLetter.id;
      }

      // Save comments
      const newComments = comments.filter(c => {
        const commentId = c.id as number | string | undefined;
        return commentId !== undefined && typeof commentId === 'string' && String(commentId).startsWith('temp-');
      });
      for (const comment of newComments) {
        await letterCommentService.create({
          letter_id: letterId,
          comment: (comment as any).comment || '',
        });
      }

      // Save reviews
      const newReviews = reviews.filter(r => typeof r.id === 'string' && String(r.id).startsWith('temp-'));
      for (const review of newReviews) {
        await letterReviewService.create({
          letter_id: letterId,
          customer_id: (review as any).customer_id,
          review: (review as any).review || '',
          rating: (review as any).rating ?? null,
        });
      }

      // Save letter items (upload files)
      const newLetterItems = letterItems.filter(item => {
        const itemId = item.id;
        return typeof itemId === 'string' && itemId.startsWith('temp-letter-');
      });
      
      for (const item of newLetterItems) {
        const tempItem = item as any;
        if (tempItem.file) {
          const fileId = `letter-${Date.now()}-${Math.random()}`;
          try {
            setUploadQueue(prev => [...prev, { file: tempItem.file, id: fileId }]);
            setUploadStatus(prev => ({ ...prev, [fileId]: 'uploading' }));
            setUploadProgress(prev => ({ ...prev, [fileId]: 0 }));

            await letterItemService.create({
              letter_id: letterId,
              attachment: tempItem.file,
              batch_no: tempItem.batch_no,
              approval_status: tempItem.approval_status,
            });
            setUploadStatus(prev => ({ ...prev, [fileId]: 'success' }));
            setUploadProgress(prev => ({ ...prev, [fileId]: 100 }));
          } catch (error) {
            console.error('Failed to upload letter item:', error);
            message.error(`Failed to upload ${tempItem.file.name}`);
            setUploadStatus(prev => ({ ...prev, [fileId]: 'error' }));
          }
        }
      }

      // Clear upload queue after a delay
      setTimeout(() => {
        setUploadQueue([]);
        setUploadStatus({});
        setUploadProgress({});
      }, 2000);

      const notice = {
        type: 'success' as const,
        message: isEdit ? 'Letter updated successfully.' : 'Letter created successfully.',
      };
      setSubmitNotice(notice);
      if (isEdit) {
        setIsEditing(false);
        await loadLetter(letterId);
      } else {
        navigate(`/letters/${letterId}/edit`, { state: { notice } });
      }
    } catch (error: any) {
      const fallbackMessage = isEdit ? 'Failed to update letter.' : 'Failed to create letter.';
      const apiMessage = error?.response?.data?.message || error?.message;
      setSubmitNotice({
        type: 'failure',
        message: apiMessage ? `${fallbackMessage} ${apiMessage}` : fallbackMessage,
      });
    } finally {
      setSubmitting(false);
    }
  };

  const formatLetterNumber = (letterId: number, referenceNumber?: string) => {
    return referenceNumber || `#${letterId.toString().padStart(7, '0')}`;
  };

  const getDisplayValue = (fieldName: string, type: 'select' | 'date' | 'number' | 'text' = 'text', options?: any[]) => {
    const value = form.getFieldValue(fieldName);
    if (value === null || value === undefined || value === '') {
      return '-';
    }
    
    if (type === 'select' && options) {
      const option = options.find(opt => opt.id === value);
      return option?.name || '-';
    }
    
    if (type === 'date') {
      if (!value) return '-';
      if (dayjs.isDayjs(value)) {
        return value.format('DD/MM/YYYY');
      }
      if (typeof value === 'string') {
        return dayjs(value).format('DD/MM/YYYY');
      }
      return '-';
    }
    
    if (type === 'number') {
      return value || '-';
    }
    
    return value || '-';
  };

  const getLetterDisplayName = () => {
    if (isEdit && id) {
      return formatLetterNumber(parseInt(id, 10), form.getFieldValue('reference_number'));
    }
    return 'New Letter';
  };

  useEffect(() => {
    if (isEdit && id && customers.length > 0 && taskTemplates.length > 0) {
      const displayName = getLetterDisplayName();
      setLetterName(displayName);
    }
  }, [isEdit, id, customers, taskTemplates, form.getFieldValue('customer_id'), form.getFieldValue('task_template_id')]);

  const formatDateTime = (dateString?: string) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();
    const hours = date.getHours();
    const minutes = date.getMinutes().toString().padStart(2, '0');
    const ampm = hours >= 12 ? 'pm' : 'am';
    const displayHours = hours % 12 || 12;
    return `${day}/${month}/${year} ${displayHours}:${minutes}${ampm}`;
  };

  const handleDelete = async () => {
    if (!id) return;
    if (!window.confirm('Are you sure you want to delete this letter?')) return;
    
    try {
      await letterService.delete(parseInt(id, 10));
                      message.success('Letter deleted');
                      navigate('/letters');
    } catch {
      message.error('Failed to delete letter');
                  }
  };

  const handleApprove = async () => {
    if (!id) return;
    setApproving(true);
    try {
      await letterService.approve(parseInt(id, 10));
      setSubmitNotice({ type: 'success', message: 'Letter approved successfully.' });
      await loadLetter(parseInt(id, 10));
    } catch (error: any) {
      const apiMessage = error?.response?.data?.message || error?.message || 'Failed to approve letter.';
      setSubmitNotice({ type: 'failure', message: apiMessage });
    } finally {
      setApproving(false);
    }
  };

  return (
    <Card bordered={false} bodyStyle={{ padding: 0 }} style={{ boxShadow: 'none' }}>
      {isEdit && letterName && (
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 24,
          paddingBottom: 16,
          borderBottom: '1px solid #f0f0f0',
        }}>
          <Title level={2} style={{ margin: 0, color: '#1890ff', fontSize: 28, fontWeight: 600 }}>
            {letterName}
          </Title>
          <Space>
            {!isEditing && canEditAny && canEditRecord && (
              <Button onClick={() => setIsEditing(true)}>
                Edit
              </Button>
            )}
            {isEdit && canApprove && showReadButton && (
              <Button
                loading={approving}
                onClick={handleApprove}
                disabled={headerStatus !== 'Unread'}
              >
                Read
              </Button>
            )}
            {isEdit && isEditing && canDelete && (
              <Button danger icon={<DeleteOutlined />} onClick={handleDelete}>
                Delete
              </Button>
            )}
          </Space>
        </div>
      )}
      
      {!isEdit && (
        <Title level={3} style={{ marginBottom: 24 }}>
          New Letter
        </Title>
      )}
      {submitNotice && (
        <div style={{ marginBottom: 16 }}>
          <Alert color={submitNotice.type} onDismiss={() => setSubmitNotice(null)}>
            {submitNotice.message}
          </Alert>
        </div>
      )}

      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        onFinishFailed={(errorInfo) => {
          const errorMessages = errorInfo.errorFields
            .flatMap((field) => field.errors)
            .filter(Boolean);
          setSubmitNotice({
            type: 'failure',
            message: errorMessages.length > 0 ? errorMessages.join(' ') : 'Please fix the form errors before submitting.',
          });
        }}
        disabled={loadingLetter || (isEdit && !isEditing)}
        onValuesChange={(changedValues) => {
          if (changedValues.customer_id !== undefined || changedValues.business_id !== undefined || changedValues.task_template_id !== undefined) {
            const displayName = getLetterDisplayName();
            setLetterName(displayName);
          }
          if (changedValues.status !== undefined) {
            setCurrentStatus(changedValues.status);
          }
        }}
      >
        <Form.Item name="type" hidden>
          <Input />
        </Form.Item>
        <Form.Item name="reference_id" hidden>
          <Input />
        </Form.Item>
        <Tabs 
          activeKey={activeTab}
          onChange={handleTabChange}
          tabBarExtraContent={
            <Form.Item shouldUpdate noStyle>
              {() => {
                // Map old status values to new ones for display
                const rawStatus = form.getFieldValue('status');
                const currentStatus = mapStatus(rawStatus);
                const statuses: Array<'Plan' | 'Unread' | 'Read'> = ['Plan', 'Unread', 'Read'];
                const statusConfig = {
                  Plan: { bg: '#E6F7FF', border: '#40A9FF', color: '#1890ff', dot: '#1890ff', label: 'Plan' },
                  Unread: { bg: '#FFF7E6', border: '#FFA940', color: '#FF8C00', dot: '#FF8C00', label: 'Sent' },
                  Read: { bg: '#F6FFED', border: '#73D13D', color: '#52C41A', dot: '#52C41A', label: 'Read' },
                };
                
                return (
                  <Space size={8}>
                    {statuses.map((status) => {
                      const isActive = currentStatus === status;
                      const config = statusConfig[status];
                      return (
                        <div
                          key={status}
                          style={{
                            padding: '4px 12px',
                            borderRadius: '12px',
                            backgroundColor: isActive ? config.bg : '#f5f5f5',
                            border: isActive ? `1px solid ${config.border}` : '1px solid #d9d9d9',
                            color: isActive ? config.color : '#595959',
                            fontWeight: isActive ? 500 : 400,
                            fontSize: '12px',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 4,
                            transition: 'all 0.2s',
                          }}
                        >
                          <span style={{
                            width: 4,
                            height: 4,
                            borderRadius: '50%',
                            backgroundColor: isActive ? config.dot : '#8c8c8c',
                          }}></span>
                          {config.label}
                        </div>
                      );
                    })}
                  </Space>
                );
              }}
            </Form.Item>
          }
        >
          {/* Overview Tab */}
          {tabVisibility.find((tab) => tab.key === 'overview')?.visible && (
          <TabPane tab="Overview" key="overview">
            <Card style={{ marginBottom: 8 }} bodyStyle={{ padding: 0 }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <tbody>
                  <tr style={{ borderBottom: '1px solid #f0f0f0' }}>
                    <td style={{ padding: '12px', width: '120px', verticalAlign: 'top', backgroundColor: '#fafafa' }}>
                      <strong>ID</strong>
                    </td>
                    <td colSpan={3} style={{ padding: '12px', verticalAlign: 'top' }}>
                      {allowEditDetails ? (
                        <Form.Item name="reference_number" style={{ margin: 0 }}>
                          <Input placeholder="Enter Reference No." />
                        </Form.Item>
                      ) : (
                        <div>{form.getFieldValue('reference_number') || '-'}</div>
                      )}
                    </td>
                  </tr>
                  {/* Customer Type - 单独一行，合并后三列 */}
                  <tr style={{ borderBottom: '1px solid #f0f0f0' }}>
                    <td style={{ padding: '12px', width: '120px', verticalAlign: 'top', backgroundColor: '#fafafa' }}>
                      <strong>Customer</strong>
                    </td>
                    <td colSpan={3} style={{ padding: '12px', verticalAlign: 'top' }}>
                      {allowEditDetails ? (
                        <Space>
                          <Button
                            type={customerType === 'personal' ? 'primary' : 'default'}
                            onClick={() => handleCustomerTypeChange('personal')}
                            size="small"
                          >
                            Personal
                          </Button>
                          <Button
                            type={customerType === 'business' ? 'primary' : 'default'}
                            onClick={() => handleCustomerTypeChange('business')}
                            size="small"
                          >
                            Business
                          </Button>
                        </Space>
                      ) : (
                        <div>
                          {customerType === 'personal' ? 'Personal' : 'Business'}
                      </div>
                      )}
                    </td>
                  </tr>
                  {/* Customer/Business - 单独一行，合并后三列 */}
                  <tr style={{ borderBottom: '1px solid #f0f0f0' }}>
                    <td style={{ padding: '12px', width: '120px', verticalAlign: 'top', backgroundColor: '#fafafa' }}>
                      <strong>{customerType === 'personal' ? 'Customer' : 'Business'}</strong>
                    </td>
                    <td colSpan={3} style={{ padding: '12px', verticalAlign: 'top' }}>
                      {allowEditDetails ? (
                        customerType === 'personal' ? (
                          <Form.Item
                            name="customer_id"
                            rules={[{ required: true, message: 'Please select customer' }]}
                            style={{ margin: 0 }}
                          >
                            <Select
                              placeholder="Select Customer"
                              allowClear
                              showSearch
                              optionFilterProp="children"
                              onChange={handleCustomerChange}
                            >
                              {customers.map((customer) => (
                                <Select.Option key={customer.id} value={customer.id}>
                                  {customer.name}
                                </Select.Option>
                              ))}
                            </Select>
                          </Form.Item>
                        ) : (
                          <Form.Item
                            name="business_id"
                            rules={[{ required: true, message: 'Please select business' }]}
                            style={{ margin: 0 }}
                          >
                            <Select
                              placeholder="Select Business"
                              allowClear
                              showSearch
                              optionFilterProp="children"
                              onChange={handleBusinessChange}
                            >
                              {businesses.map((business) => (
                                <Select.Option key={business.id} value={business.id}>
                                  {business.name}
                                </Select.Option>
                              ))}
                            </Select>
                          </Form.Item>
                        )
                      ) : (
                        <div>
                          {customerType === 'personal' 
                            ? getDisplayValue('customer_id', 'select', customers)
                            : getDisplayValue('business_id', 'select', businesses)
                          }
                      </div>
                      )}
                    </td>
                  </tr>
                  {/* Subject - 单独一行，合并后三列 */}
                  <tr style={{ borderBottom: '1px solid #f0f0f0' }}>
                    <td style={{ padding: '12px', width: '120px', verticalAlign: 'top', backgroundColor: '#fafafa' }}>
                      <strong>Subject</strong>
                    </td>
                    <td colSpan={3} style={{ padding: '12px', verticalAlign: 'top' }}>
                      {allowEditDetails ? (
                        <Form.Item name="task_template_id" style={{ margin: 0 }}>
                          <Select
                            placeholder="Select Task"
                            allowClear
                            onChange={(value) => handleTaskTemplateChange(value || null)}
                          >
                            {taskTemplates.map((template) => (
                              <Select.Option key={template.id} value={template.id}>
                                {template.name}
                              </Select.Option>
                            ))}
                          </Select>
                        </Form.Item>
                      ) : (
                        <div>
                          {getDisplayValue('task_template_id', 'select', taskTemplates)}
                      </div>
                      )}
                    </td>
                  </tr>
                  {/* Task No. - 单独一行，合并后三列 */}
                  <tr style={{ borderBottom: '1px solid #f0f0f0' }}>
                    <td style={{ padding: '12px', width: '120px', verticalAlign: 'top', backgroundColor: '#fafafa' }}>
                      <strong>Task No.</strong>
                    </td>
                    <td colSpan={3} style={{ padding: '12px', verticalAlign: 'top' }}>
                      {allowEditDetails ? (
                        <Form.Item name="task_id" style={{ margin: 0 }}>
                          <Select
                            placeholder="Select Task"
                            allowClear
                            showSearch
                            optionFilterProp="children"
                            onChange={(value) => handleTaskChange(value as number | undefined)}
                          >
                            {getAvailableTasks().map((task) => {
                              const label = formatTaskNumber(task.id, task.reference_number);
                              const templateLabel = task.taskTemplate?.name ? ` - ${task.taskTemplate.name}` : '';
                              return (
                                <Select.Option key={task.id} value={task.id}>
                                  {`${label}${templateLabel}`}
                                </Select.Option>
                              );
                            })}
                          </Select>
                        </Form.Item>
                      ) : (
                        <div>
                          <Form.Item shouldUpdate noStyle>
                            {({ getFieldValue }) => {
                              const taskId = getFieldValue('task_id');
                              if (!taskId) {
                                return '-';
                              }
                              const task = tasks.find((item) => item.id === taskId);
                              const label = formatTaskNumber(taskId, task?.reference_number);
                              return (
                                <Button
                                  type="link"
                                  disabled={false}
                                  onClick={() => navigate(`/tasks/${taskId}/edit`)}
                                  style={{ padding: 0, height: 'auto' }}
                                >
                                  {label}
                                </Button>
                              );
                            }}
                          </Form.Item>
                        </div>
                      )}
                    </td>
                  </tr>
                  {/* Create 和 Due - 同一行，各占两列 */}
                  <tr style={{ borderBottom: '1px solid #f0f0f0' }}>
                    <td style={{ padding: '12px', width: '120px', verticalAlign: 'top', backgroundColor: '#fafafa' }}>
                      <strong>Create</strong>
                    </td>
                    <td style={{ padding: '12px', width: 'calc((100% - 240px) / 2)', verticalAlign: 'top' }}>
                      {allowEditDetails ? (
                        <Form.Item name="create_date" style={{ margin: 0 }}>
                          <DatePicker
                            style={{ width: '100%' }}
                            format="DD/MM/YYYY"
                            placeholder="Enter Create Date"
                            allowClear
                            onChange={(date) => {
                              form.setFieldsValue({ create_date: date || null });
                            }}
                          />
                        </Form.Item>
                      ) : (
                        <div>
                          {getDisplayValue('create_date', 'date')}
                      </div>
                      )}
                    </td>
                    <td style={{ padding: '12px', width: '120px', verticalAlign: 'top', backgroundColor: '#fafafa' }}>
                      <strong>Due</strong>
                    </td>
                    <td style={{ padding: '12px', width: 'calc((100% - 240px) / 2)', verticalAlign: 'top' }}>
                      {allowEditDetails ? (
                        <Form.Item name="due_date" style={{ margin: 0 }}>
                          <DatePicker
                            style={{ width: '100%' }}
                            format="DD/MM/YYYY"
                            placeholder="Enter Due Date"
                            allowClear
                            onChange={(date) => {
                              form.setFieldsValue({ due_date: date || null });
                            }}
                          />
                        </Form.Item>
                      ) : (
                        <div>
                          {getDisplayValue('due_date', 'date')}
                      </div>
                      )}
                    </td>
                  </tr>
                  {/* Advisor - 单独一行，合并后三列 */}
                  <tr style={{ borderBottom: '1px solid #f0f0f0' }}>
                    <td style={{ padding: '12px', width: '120px', verticalAlign: 'top', backgroundColor: '#fafafa' }}>
                      <strong>Advisor</strong>
                    </td>
                    <td colSpan={3} style={{ padding: '12px', verticalAlign: 'top' }}>
                      <Input
                        value={
                          (customerType === 'business'
                            ? selectedBusiness?.advisorStaff?.name
                              || selectedBusiness?.advisor_staff?.name
                            : selectedCustomer?.advisorStaff?.name
                              || selectedCustomer?.advisor_staff?.name
                          ) || ''
                        }
                        placeholder="Direct link after select customer"
                        disabled
                      />
                    </td>
                  </tr>
                </tbody>
              </table>
            </Card>
          </TabPane>
          )}

          {/* Checklist Tab */}
          {tabVisibility.find((tab) => tab.key === 'checklist')?.visible && (
          <TabPane tab="Checklist" key="checklist">
            <Card
              title="Sub-Tasks"
              bodyStyle={{ padding: '16px' }}
              extra={
                allowEditChecklist ? (
                  <Button
                    type="link"
                    icon={<PlusOutlined />}
                    onClick={handleAddSubTask}
                  >
                    Add
                  </Button>
                ) : null
              }
            >
              {letterSubTasks.map((subTask, index) => (
                <div key={subTask.id || index} style={{ padding: '12px 0', borderBottom: '1px solid #f0f0f0' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                    <Checkbox
                      checked={subTask.completed}
                      disabled={!allowEditChecklist}
                      onChange={async (e) => {
                        if (!allowEditChecklist) return;
                        const checked = e.target.checked;
                        const now = new Date().toISOString();
                        setLetterSubTasks(prev =>
                          prev.map(st =>
                            st.id === subTask.id
                              ? {
                                  ...st,
                                  completed: checked,
                                  updated_at: checked ? now : st.updated_at,
                                  updated_by: checked ? user?.id : st.updated_by,
                                  updater: checked && user ? { id: user.id, name: user.name, email: user.email || '' } : st.updater,
                                }
                              : st
                          )
                        );
                        if (isEdit && id && typeof subTask.id === 'number') {
                          try {
                            const payloadSubTasks = letterSubTasks
                              .map((st) => ({
                                id: typeof st.id === 'number' ? st.id : undefined,
                                name: st.name,
                                completed: st.id === subTask.id ? checked : st.completed,
                              }))
                              .filter((st) => typeof st.id === 'number');
                            const updatedLetter = await letterService.update(parseInt(id, 10), {
                              sub_tasks: payloadSubTasks as any,
                            });
                            const updatedRawSubTasks =
                              (updatedLetter as any).subTasks || (updatedLetter as any).sub_tasks || [];
                            setLetterSubTasks(normalizeLetterSubTasks(updatedRawSubTasks));
                          } catch {
                            message.error('Failed to update sub task');
                          }
                        }
                      }}
                    />
                    <Input
                      value={subTask.name || ''}
                      disabled={!allowEditChecklist}
                      onChange={(e) => {
                        if (!allowEditChecklist) return;
                        setLetterSubTasks(prev =>
                          prev.map(st =>
                            st.id === subTask.id
                              ? { ...st, name: e.target.value }
                              : st
                          )
                        );
                      }}
                      style={{ flex: 1 }}
                      placeholder="Enter sub task name"
                    />
                    {allowEditChecklist && canDelete && (
                      <Button
                        type="link"
                        danger
                        icon={<DeleteOutlined />}
                        onClick={() => {
                          setLetterSubTasks(prev => prev.filter(st => st.id !== subTask.id));
                        }}
                        size="small"
                      />
                    )}
                  </div>
                  {subTask.completed && (
                    <div style={{ marginLeft: 24, fontSize: '12px', color: '#999' }}>
                      By {subTask.updater?.name || subTask.creator?.name || user?.name || 'Unknown'}{' '}
                      {subTask.updated_at
                        ? formatDateTime(subTask.updated_at)
                        : subTask.created_at
                        ? formatDateTime(subTask.created_at)
                        : ''}
                    </div>
                  )}
                            </div>
                          ))}
              {letterSubTasks.length === 0 && (
                            <div style={{ color: '#999', textAlign: 'center', padding: '20px 0' }}>
                  No sub projects.tasks. Click 'Add' to add a sub task.
                            </div>
                          )}
            </Card>
          </TabPane>
          )}

          {/* Letter Tab */}
          {tabVisibility.find((tab) => tab.key === 'letter')?.visible && (
          <TabPane tab="Letter" key="letter">
            <Card
              title="Letters"
              bodyStyle={{ padding: '16px' }}
            >
              {/* Upload Progress */}
              {uploadQueue.length > 0 && (
                <div style={{ marginBottom: 16, padding: '12px', background: '#fafafa', borderRadius: 4 }}>
                  <div style={{ marginBottom: 8 }}>
                    <Text strong>Uploading {uploadQueue.length} file(s)</Text>
                  </div>
                  <div style={{ maxHeight: 200, overflowY: 'auto' }}>
                    {uploadQueue.map((item) => {
                      const status = uploadStatus[item.id] || 'pending';
                      const progress = uploadProgress[item.id] || 0;
                      return (
                        <div key={item.id} style={{ marginBottom: 8 }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                            <Text style={{ fontSize: '12px' }} ellipsis>
                              {item.file.name}
                            </Text>
                            <Tag
                              color={
                                status === 'success' ? 'green' :
                                status === 'error' ? 'red' :
                                status === 'uploading' ? 'blue' : 'default'
                              }
                            >
                              {status === 'success' ? 'Done' :
                               status === 'error' ? 'Failed' :
                               status === 'uploading' ? 'Uploading' : 'Pending'}
                            </Tag>
                          </div>
                          {status === 'uploading' && (
                            <Progress percent={progress} size="small" />
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
              <div style={{ border: '1px solid #f0f0f0', borderRadius: 6, padding: 12, marginBottom: 16 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12, gap: 8 }}>
                  <Space size={8}>
                    {getLetterStatusTag(batch1Status)}
                    {renderLetterStatusActions(1, batch1Status, batch1Items.length > 0)}
                  </Space>
                  {isEdit ? (
                    allowEditLetter ? renderLetterUploadButton(1, batch1Status !== 'pending') : null
                  ) : (
                    <Text type="secondary">Save letter first</Text>
                  )}
                </div>
                {renderLetterItemList(batch1Items)}
              </div>
              {showBatch2 && (
                <div style={{ border: '1px solid #f0f0f0', borderRadius: 6, padding: 12, marginBottom: 16 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12, gap: 8 }}>
                    <Space size={8}>
                      {getLetterStatusTag(batch2Status)}
                      {renderLetterStatusActions(2, batch2Status, batch2Items.length > 0)}
                    </Space>
                    {isEdit ? (
                      allowEditLetter ? renderLetterUploadButton(2, !allowBatch2 || batch2Status !== 'pending') : null
                    ) : (
                      <Text type="secondary">Save letter first</Text>
                    )}
                  </div>
                  {renderLetterItemList(batch2Items)}
                </div>
              )}
              {showBatch3 && (
                <div style={{ border: '1px solid #f0f0f0', borderRadius: 6, padding: 12 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12, gap: 8 }}>
                    <Space size={8}>
                      {getLetterStatusTag(batch3Status)}
                      {renderLetterStatusActions(3, batch3Status, batch3Items.length > 0)}
                    </Space>
                    {isEdit ? (
                      allowEditLetter ? renderLetterUploadButton(3, !allowBatch3 || batch3Status !== 'pending') : null
                    ) : (
                      <Text type="secondary">Save letter first</Text>
                    )}
                  </div>
                  {renderLetterItemList(batch3Items)}
                </div>
              )}
            </Card>
          </TabPane>
          )}

          {/* Comment Tab */}
          {tabVisibility.find((tab) => tab.key === 'comment')?.visible && (
          <TabPane tab="Comment" key="comment">
            <Card
              title="Comments"
              bodyStyle={{ padding: '16px' }}
            >
              <div style={{ marginBottom: 16 }}>
                {comments.map((comment, index) => (
                  <div
                    key={comment.id || index}
                    style={{
                      padding: '12px 0',
                      borderBottom: '1px solid #f0f0f0',
                    }}
                  >
                    <div style={{ marginBottom: 8, whiteSpace: 'pre-wrap' }}>
                      <Text>{comment.comment || '-'}</Text>
                    </div>
                    <div style={{ fontSize: '12px', color: '#999' }}>
                      By {comment.user?.name || 'Unknown'} • {formatDateTime(comment.created_at)}
                      {canDelete && (
                <Button
                  type="link"
                          danger
                          size="small"
                          icon={<DeleteOutlined />}
                          onClick={() => handleDeleteComment(comment.id)}
                          style={{ marginLeft: 8, padding: 0 }}
                        />
                      )}
                    </div>
                  </div>
                ))}
                {comments.length === 0 && (
                  <div style={{ color: '#999', textAlign: 'center', padding: '20px 0' }}>
                    No comments yet.
                  </div>
                )}
              </div>
              {allowEditComment && (
                <div style={{ marginTop: 16, paddingTop: 16, borderTop: '1px solid #f0f0f0' }}>
                  <TextArea
                    rows={6}
                    placeholder="Enter comment..."
                    value={commentText}
                    onChange={(e) => setCommentText(e.target.value)}
                  />
                  <Button
                    type="primary"
                    onClick={handleAddComment}
                    style={{ marginTop: 12, width: '100%' }}
                    disabled={!commentText.trim()}
                >
                    Add Comment
                </Button>
                </div>
              )}
            </Card>
          </TabPane>
          )}

          {/* Review Tab */}
          {tabVisibility.find((tab) => tab.key === 'review')?.visible && (
          <TabPane tab="Review" key="review">
            <Card title="Reviews" bodyStyle={{ padding: '16px' }}>
              <div style={{ marginBottom: 24 }}>
                <div style={{ marginBottom: 12, fontWeight: 500, fontSize: '16px' }}>Manager Reviews</div>
                <div style={{ marginBottom: 16 }}>
                  {reviews.filter((review: LetterReview) => !review.customer_id).map((review, index) => (
                    <div
                      key={review.id || index}
                      style={{
                        padding: '12px 0',
                        borderBottom: '1px solid #f0f0f0',
                      }}
                    >
                      <div style={{ marginBottom: 8 }}>
                        <Rate disabled value={review.rating || 0} />
                        <div style={{ marginTop: 6, whiteSpace: 'pre-wrap' }}>
                          <Text>{review.review || '-'}</Text>
                        </div>
                      </div>
                      <div style={{ fontSize: '12px', color: '#999' }}>
                        By {review.creator?.name || 'Unknown'} • {formatDateTime(review.created_at)}
                        {canDelete && (
                          <Button
                            type="link"
                            danger
                            size="small"
                            icon={<DeleteOutlined />}
                            onClick={() => {
                              setReviews(prev => prev.filter(r => r.id !== review.id));
                            }}
                            style={{ marginLeft: 8, padding: 0 }}
                          />
                        )}
                      </div>
                    </div>
                  ))}
                  {reviews.filter((review: LetterReview) => !review.customer_id).length === 0 && (
                    <div style={{ color: '#999', textAlign: 'center', padding: '20px 0' }}>
                      No manager reviews yet.
                    </div>
                  )}
                </div>
                {allowEditReview && (
                  <div style={{ marginTop: 16, paddingTop: 16, borderTop: '1px solid #f0f0f0' }}>
                    <div style={{ marginBottom: 8 }}>
                      <Rate value={reviewRating || 0} onChange={(value) => setReviewRating(value || null)} />
                    </div>
                    <TextArea
                      rows={6}
                      placeholder="Enter review..."
                      value={reviewText}
                      onChange={(e) => setReviewText(e.target.value)}
                    />
                    <Button
                      type="primary"
                      onClick={handleAddReview}
                      style={{ marginTop: 12, width: '100%' }}
                      disabled={!reviewText.trim() || !reviewRating}
                    >
                      Add Review
                    </Button>
                  </div>
                )}
              </div>

              <div style={{ borderTop: '1px solid #f0f0f0', paddingTop: 24 }}>
                <div style={{ marginBottom: 12, fontWeight: 500, fontSize: '16px' }}>Customer Reviews</div>
                {reviews.filter((review: LetterReview) => review.customer_id).length > 0 ? (
                  <div>
                    {reviews.filter((review: LetterReview) => review.customer_id).map((review, index) => (
                      <div
                        key={review.id || index}
                        style={{
                          padding: '12px 0',
                          borderBottom: '1px solid #f0f0f0',
                        }}
                      >
                        <div style={{ marginBottom: 8 }}>
                          <Rate disabled value={review.rating || 0} />
                          <div style={{ marginTop: 6, whiteSpace: 'pre-wrap' }}>
                            <Text>{review.review || '-'}</Text>
                          </div>
                        </div>
                        <div style={{ fontSize: '12px', color: '#999' }}>
                          By {review.customer?.name || review.creator?.name || 'Unknown'} • {formatDateTime(review.created_at)}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div style={{ color: '#999', textAlign: 'center', padding: '20px 0' }}>
                    No customer reviews submitted yet
                  </div>
                )}
              </div>
            </Card>
          </TabPane>
          )}
        </Tabs>

        <div style={{
          padding: '16px 24px',
          borderTop: '1px solid #f0f0f0',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginTop: 24,
          position: 'sticky',
          bottom: 0,
          backgroundColor: '#fff',
          zIndex: 10,
          
        }}>
          <Form.Item shouldUpdate={(prevValues, currentValues) => prevValues.is_sent !== currentValues.is_sent || prevValues.status !== currentValues.status} noStyle>
            {({ getFieldValue }) => {
              const currentStatus = getFieldValue('status');
              // Disable checkbox if status is Read, or if not editing
              const isDisabled = (!isEditing && isEdit) || currentStatus === 'Read';
              const isReadVisible = currentStatus === 'Read';
              const isReadDisabled = !isEditing && isEdit;
              
              if (isEdit && !isEditing) {
                return null;
              }

              return (
                <Space size={12}>
                  {canApprove && (
                    <Form.Item name="is_sent" valuePropName="checked" noStyle>
                      <Checkbox 
                        onChange={(e) => {
                          const checked = e.target.checked;
                          // Form.Item will automatically update is_sent
                          // We just need to update status
                          if (checked) {
                            form.setFieldsValue({ status: 'Unread' });
                          } else {
                            // If unchecking and current status is Unread, revert to Plan
                            if (currentStatus === 'Unread') {
                              form.setFieldsValue({ status: 'Plan' });
                            }
                          }
                        }}
                        disabled={isDisabled}
                      >
                        Sent
                      </Checkbox>
                    </Form.Item>
                  )}
                  {isReadVisible && (
                    <Checkbox
                      checked
                      onChange={(e) => {
                        if (!e.target.checked) {
                          form.setFieldsValue({ status: 'Plan', is_sent: false });
                        }
                      }}
                      disabled={isReadDisabled}
                    >
                      Read
                    </Checkbox>
                  )}
                </Space>
              );
            }}
          </Form.Item>
          <Space>
          {isEditing && (
            <>
              <Button 
                onClick={() => {
                if (isEdit && id) {
                  setIsEditing(false);
                  // Reload letter data to reset form
                  loadLetter(parseInt(id, 10));
                } else {
                  // For create mode, navigate back
                  navigate('/letters');
                }
                }}
                disabled={false}
              >
                Cancel
              </Button>
              <Button 
                type="primary" 
                onClick={() => {
                  form.submit();
                }} 
                loading={submitting}
              >
                {isEdit ? 'Save' : 'Create'}
              </Button>
            </>
          )}
          </Space>
        </div>
      </Form>
      
      {/* Buttons outside Form to avoid disabled state */}
      {!isEditing && isEdit && (
        <div style={{
          padding: '16px 24px',
          borderTop: '1px solid #f0f0f0',
          display: 'flex',
          justifyContent: 'flex-end',
          marginTop: 24,
          position: 'sticky',
          bottom: 0,
          backgroundColor: '#fff',
          zIndex: 10,
        }}>
          <Button onClick={() => navigate('/letters')}>Cancel</Button>
        </div>
      )}

    </Card>
  );
};
